import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft, faCaretRight } from "@fortawesome/sharp-solid-svg-icons";
import { faArrowRotateLeft, faBriefcaseArrowRight } from "@fortawesome/sharp-light-svg-icons";
import {
  Box,
  Button,
  ErrorOutlineIcon,
  Grid,
  IconButton,
  PlatformDialog,
  pxToRem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@platform/style";
import React, { useCallback, useEffect, useState } from "react";
import { submitErrorLog } from "./apiClient";
import { lighten } from "@mui/material/styles";
import { BackendFailureAlertFields, getUserDetails } from "../../platform-core";

export interface BackendFailureAlertProps {
  failures?: BackendFailureAlertFields[];
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  message?: string;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  retryFunction?: () => void;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  reportable?: boolean | undefined;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  correlationId?: string;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  component?: string;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  componentStack?: string;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  exceptionInformation?: string;
  /** @deprecated use failures field instead. It is an Array of BackendFailureAlertFields */
  commandInformation?: string;
}

export const BackendFailureAlert = ({
  failures,
  message,
  retryFunction,
  reportable = true,
  correlationId,
  component,
  componentStack,
  exceptionInformation,
  commandInformation,
}: BackendFailureAlertProps) => {
  const [failuresList, setFailuresList] = useState<BackendFailureAlertFields[]>([]);
  const [failureSubmitted, setFailureSubmitted] = useState(false);
  const [failureCommentOpen, setFailureCommentOpen] = useState(false);
  const [failureComments, setFailureComments] = useState("");
  const [failureSubmissionFailure, setFailureSubmissionFailure] = useState(false);
  const [failureCount, setFailureCount] = useState(failuresList.length);
  const [currentFailure, setCurrentFailure] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    if (failures) {
      setFailuresList(failures);
      setFailureCount(failures.length);
      setCurrentFailure(0);
    } else {
      let failureArray: BackendFailureAlertFields[] = [];
      let backendFailureAlert: BackendFailureAlertFields = {
        message: message,
        retryFunction: retryFunction,
        reportable: reportable,
        correlationId: correlationId,
        component: component,
        componentStack: componentStack,
        exceptionInformation: exceptionInformation,
        commandInformation: commandInformation,
      };
      failureArray.push(backendFailureAlert);
      setFailuresList(failureArray);
      setFailureCount(1);
    }
  }, [failures]);

  function closeErrorComment() {
    setFailureCommentOpen(false);
  }
  function openErrorComment() {
    setFailureCommentOpen(true);
  }
  function closeSubmissionError() {
    setFailureSubmissionFailure(false);
  }
  const submitErrorLogFunc = useCallback(async () => {
    const userDetail = getUserDetails();
    try {
      const response = await submitErrorLog(
        userDetail.entityName,
        userDetail.uid,
        failureComments,
        failuresList[currentFailure].correlationId,
        failuresList[currentFailure].component,
        failuresList[currentFailure].componentStack,
        failuresList[currentFailure].exceptionInformation,
        failuresList[currentFailure].commandInformation
      );
      if (!response.ok) {
        throw new Error(
          JSON.stringify({
            statusCode: response.status,
            statusText: response.statusText,
            body: await response.json(),
          })
        );
      }
      setFailureSubmitted(true);
      closeErrorComment();
    } catch (error) {
      setFailureSubmitted(true);
      closeErrorComment();
      setFailureSubmissionFailure(true);
    }
  }, [failureComments, failuresList, currentFailure, closeErrorComment]);

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFailureComments(event.target.value);
  };

  const handleNextClick = () => {
    if (currentFailure + 1 < failureCount) {
      setCurrentFailure(currentFailure + 1);
    }
  };

  const handlePreviousClick = () => {
    if (currentFailure > 0) {
      setCurrentFailure(currentFailure - 1);
    }
  };

  return (
    <>
      <Grid
        container
        role="alert"
        direction="row"
        justifyContent="space-around"
        alignItems="flex-start"
        width="100%"
        sx={{
          pl: "0px",
          border: "1px solid",
          borderColor: "error.main",
          backgroundColor: theme.palette.error.main + "1f",
        }}
      >
        {failuresList.length > 1 && (
          <>
            <Grid item>
              <Button
                onClick={handlePreviousClick}
                aria-label="previous button"
                sx={{
                  height: "36px",
                  minWidth: "30px",
                  mx: "0px",
                  px: "4px",
                  borderRadius: "0px",
                  backgroundColor: "error.main",
                  color:
                    currentFailure === 0
                      ? lighten(theme.palette.error.main, 0.5)
                      : "primary.contrastText",
                  "&.Mui-focusVisible": {
                    backgroundColor: theme.palette.error.main + "80",
                  },
                  "&:focus": {
                    backgroundColor: theme.palette.error.main + "80",
                  },
                  "&.Mui-focused": { backgroundColor: theme.palette.error.main + "80" },
                  "&:hover": {
                    backgroundColor: theme.palette.error.main + "80",
                  },
                }}
              >
                <FontAwesomeIcon icon={faCaretLeft} size={"lg"} />
              </Button>
              <Button
                onClick={handleNextClick}
                aria-label="next button"
                sx={{
                  height: "36px",
                  minWidth: "30px",
                  ml: "0px",
                  px: "4px",
                  borderRadius: "0px",
                  backgroundColor: "error.main",
                  color:
                    currentFailure === failureCount - 1
                      ? lighten(theme.palette.error.main, 0.5)
                      : "primary.contrastText",
                  "&.Mui-focusVisible": {
                    backgroundColor: theme.palette.error.main + "80",
                  },
                  "&:focus": {
                    backgroundColor: theme.palette.error.main + "80",
                  },
                  "&.Mui-focused": { backgroundColor: theme.palette.error.main + "80" },
                  "&:hover": {
                    backgroundColor: theme.palette.error.main + "80",
                  },
                }}
              >
                <FontAwesomeIcon icon={faCaretRight} size={"lg"} />
              </Button>
            </Grid>
            <Grid item sx={{ pt: "8px", px: "8px" }}>
              <Typography
                variant={"body3"}
                color={theme?.palette?.text.secondary}
                fontSize={pxToRem(15)}
                fontWeight={400}
              >
                ({currentFailure + 1}/{failureCount})
              </Typography>
            </Grid>
          </>
        )}
        {failuresList.length < 2 && (
          <Grid item xs="auto" sx={{ pt: "6px", color: "error.main", px: "8px", height: "36px" }}>
            <ErrorOutlineIcon sx={{ fontSize: "24px" }} />
          </Grid>
        )}
        <Grid
          item
          xs="auto"
          sx={{
            pt: "8px",
            pl: failureCount < 2 ? "8px" : "0px",
            pr: "16px",
            height: "36px",
          }}
        >
          <Typography
            variant={"body3"}
            color={theme?.palette?.text.secondary}
            fontSize={pxToRem(15)}
            fontWeight={400}
          >
            {failuresList[currentFailure]?.message || "An unexpected error has occurred."}
          </Typography>
        </Grid>
        <Grid item xs></Grid>

        {failuresList.length > 0 &&
          (failuresList[currentFailure].reportable === undefined ||
            failuresList[currentFailure].reportable) && (
            <>
              <Grid item xs={"auto"} sx={{ pt: "4px", mr: "16px" }}>
                <Tooltip title="Report Error to Client Support">
                  <IconButton
                    aria-label={"submiterror"}
                    color={"inherit"}
                    type={"button"}
                    size={"small"}
                    onClick={openErrorComment}
                    disabled={failureSubmitted}
                    sx={{ mr: "8px" }}
                  >
                    <FontAwesomeIcon icon={faBriefcaseArrowRight} fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                {failuresList[currentFailure]?.retryFunction && (
                  <Tooltip title="Retry">
                    <IconButton
                      aria-label={"retry"}
                      color={"inherit"}
                      type={"button"}
                      size={"small"}
                      onClick={failuresList[currentFailure]?.retryFunction}
                    >
                      <FontAwesomeIcon icon={faArrowRotateLeft} fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </>
          )}
      </Grid>

      <PlatformDialog
        open={failureCommentOpen}
        onClose={closeErrorComment}
        title={"Error Comments"}
        submitLabel={"Submit"}
        onSubmit={submitErrorLogFunc}
      >
        <Grid
          container
          spacing={1}
          direction="row"
          alignContent={"space-around"}
          display={"inline"}
        >
          <Grid item>
            <TextField
              autoFocus
              required
              margin="dense"
              id="comment"
              name="comment"
              fullWidth
              variant="outlined"
              autoComplete="off"
              label="Steps and Information"
              onChange={handleCommentChange}
              placeholder={"Enter Comments"}
            />
          </Grid>
        </Grid>
      </PlatformDialog>
      <PlatformDialog
        open={failureSubmissionFailure}
        title={"Error Submission Failure"}
        onClose={closeSubmissionError}
      >
        <Typography sx={{ textAlign: "left", marginTop: pxToRem(16) }}>
          An unexpected error has occurred. Please contact your Client Administrator.
          <Box component={"div"} sx={{ marginTop: pxToRem(8) }}>
            Correlation ID: {failuresList[currentFailure]?.correlationId || "Unavailable"}
          </Box>
        </Typography>
      </PlatformDialog>
    </>
  );
};
